import React, { useState } from 'react';
import { Artifact, ArtifactType } from '../../types/artifact';
import { TextArtifactViewer } from './viewers/TextArtifactViewer';
import { CodeArtifactViewer } from './viewers/CodeArtifactViewer';
import { ImageArtifactViewer } from './viewers/ImageArtifactViewer';
import { IdeaArtifactViewer } from './viewers/IdeaArtifactViewer';
import { CritiqueArtifactViewer } from './viewers/CritiqueArtifactViewer';
import { SolutionArtifactViewer } from './viewers/SolutionArtifactViewer';
import { DocumentArtifactViewer } from './viewers/DocumentArtifactViewer';
import { DiagramArtifactViewer } from './viewers/DiagramArtifactViewer';
import { MermaidDiagramViewer } from './viewers/MermaidDiagramViewer';
import { PersonaChatArtifactViewer } from './viewers/PersonaChatArtifactViewer';
import { useTheme } from '../../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

// Create a custom component to handle mermaid code blocks
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match && match[1] ? match[1] : '';
  
  // Special handling for mermaid code blocks
  if (language === 'mermaid') {
    return (
      <div className="mermaid-diagram">
        <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto">
          <code className="text-sm">{children}</code>
        </pre>
        <div className="text-xs text-gray-500 mt-1">
          Mermaid diagram (view source)
        </div>
      </div>
    );
  }
  
  // For all other languages, use default rendering
  return !inline ? (
    <pre className={className} {...props}>
      <code className={className} {...props}>
        {children}
      </code>
    </pre>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

interface ArtifactViewerProps {
  artifact: Artifact;
}

export const ArtifactViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const [viewMode, setViewMode] = useState<string>('default');
  const { darkMode } = useTheme();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  
  // Function to copy artifact content to clipboard
  const copyArtifactContent = () => {
    let contentToCopy = '';
    
    if (typeof artifact.content === 'string') {
      contentToCopy = artifact.content;
    } else if (artifact.content && typeof artifact.content === 'object') {
      try {
        contentToCopy = JSON.stringify(artifact.content, null, 2);
      } catch (e) {
        contentToCopy = 'Error: Could not stringify content';
      }
    } else {
      contentToCopy = 'No content available';
    }
    
    navigator.clipboard.writeText(contentToCopy);
    setCopiedToClipboard(true);
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedToClipboard(false);
    }, 2000);
  };
  
  const getAvailableViewModes = () => {
    const modes: Record<string, string> = {
      default: 'Default'
    };
    
    switch (artifact.type) {
      case ArtifactType.CODE:
        modes.editor = 'Editor';
        modes.preview = 'Preview';
        break;
      case ArtifactType.DOCUMENT:
        modes.formatted = 'Formatted';
        modes.raw = 'Raw';
        break;
      case ArtifactType.DIAGRAM:
        modes.interactive = 'Interactive';
        modes.static = 'Static';
        break;
    }
    
    return modes;
  };
  
  const renderViewer = () => {
    // First check if we have a special view mode
    if (viewMode !== 'default') {
      switch (artifact.type) {
        case ArtifactType.CODE:
          if (viewMode === 'editor') {
            return <CodeArtifactViewer artifact={artifact} mode="editor" />;
          } else if (viewMode === 'preview') {
            return <CodeArtifactViewer artifact={artifact} mode="preview" />;
          }
          break;
        case ArtifactType.DOCUMENT:
          if (viewMode === 'formatted') {
            return <DocumentArtifactViewer artifact={artifact} mode="formatted" />;
          } else if (viewMode === 'raw') {
            return <DocumentArtifactViewer artifact={artifact} mode="raw" />;
          }
          break;
        case ArtifactType.DIAGRAM:
          if (viewMode === 'interactive') {
            return <DiagramArtifactViewer artifact={artifact} mode="interactive" />;
          } else if (viewMode === 'static') {
            return <DiagramArtifactViewer artifact={artifact} mode="static" />;
          }
          break;
      }
    }
    
    // For markdown content types when no specific viewer is available
    if (typeof artifact.content === 'string' && 
        (artifact.type === 'markdown' || 
         artifact.type === 'text' || 
         artifact.type.includes('markdown') || 
         artifact.type.includes('text'))) {
      return (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              // Configure rehype-highlight to ignore mermaid
              [(rehypeHighlight as any), { 
                ignoreMissing: true, 
                subset: false // Don't throw on unknown languages
              }]
            ]}
            components={{
              code: CodeBlock // Use our custom code block component
            }}
          >
            {artifact.content}
          </ReactMarkdown>
        </div>
      );
    }
    
    // metadata.format overrides type-based viewer selection.
    // This lets AI-native tooling set type (semantic role) independently
    // of how the content should be rendered.
    const format = artifact.metadata?.format as string | undefined;

    // Format-driven viewer — checked first, before type
    if (format) {
      switch (format) {
        case 'json':
          return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'json' } }} />;
        case 'yaml':
        case 'yml':
          return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'yaml' } }} />;
        case 'toml':
          return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'toml' } }} />;
        case 'csv':
        case 'tsv':
          return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'plaintext' } }} />;
        case 'prompt':
          return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'markdown' } }} />;
        case 'mermaid':
          return <MermaidDiagramViewer artifact={artifact} />;
        case 'code':
          return <CodeArtifactViewer artifact={artifact} />;
        case 'image':
          return <ImageArtifactViewer artifact={artifact} />;
        case 'markdown':
        case 'text':
          return <TextArtifactViewer artifact={artifact} />;
        // unknown format — fall through to type-based routing below
      }
    }

    // Type-based viewer — semantic role fallback
    switch (artifact.type) {
      case ArtifactType.TEXT:
        return <TextArtifactViewer artifact={artifact} />;
      case ArtifactType.CODE:
        return <CodeArtifactViewer artifact={artifact} />;
      case ArtifactType.IMAGE:
        return <ImageArtifactViewer artifact={artifact} />;
      case ArtifactType.IDEA:
        return <IdeaArtifactViewer artifact={artifact} />;
      case ArtifactType.CRITIQUE:
        return <CritiqueArtifactViewer artifact={artifact} />;
      case ArtifactType.SOLUTION:
        return <SolutionArtifactViewer artifact={artifact} />;
      case ArtifactType.DOCUMENT:
        return <DocumentArtifactViewer artifact={artifact} />;
      case ArtifactType.DIAGRAM:
        return <DiagramArtifactViewer artifact={artifact} />;
      case ArtifactType.JSON:
        return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'json' } }} />;
      case ArtifactType.YAML:
        return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'yaml' } }} />;
      case ArtifactType.TOML:
        return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'toml' } }} />;
      case ArtifactType.CSV:
        return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'plaintext' } }} />;
      case ArtifactType.PROMPT:
        return <CodeArtifactViewer artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'markdown' } }} />;
      case ArtifactType.MARKDOWN:
        // Markdown artifacts expect content to be a plain string — the
        // TextArtifactViewer already drives ReactMarkdown for that shape.
        return <TextArtifactViewer artifact={artifact} />;
      case ArtifactType.PERSONA_CHAT:
        return <PersonaChatArtifactViewer artifact={artifact} />;
      default:
        return <TextArtifactViewer artifact={artifact} />;
    }
  };
  
  const viewModes = getAvailableViewModes();
  const hasMultipleViewModes = Object.keys(viewModes).length > 1;
  
  return (
    <div className={`artifact-viewer rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div>
          <h3 className="font-medium text-lg">{artifact.name}</h3>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {artifact.type} • Created by {artifact.agent_name || artifact.created_by || artifact.persona_id} • Version {artifact.version || '1'}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            className={`px-3 py-1 rounded text-sm flex items-center ${
              darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
            onClick={copyArtifactContent}
          >
            {copiedToClipboard ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Content
              </>
            )}
          </button>
        
          {hasMultipleViewModes && (
            <div className="flex space-x-1">
              {Object.entries(viewModes).map(([mode, label]) => (
                <button
                  key={mode}
                  className={`px-3 py-1 text-sm rounded ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : darkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => setViewMode(mode)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="p-4">
        {renderViewer()}
      </div>
      
      {artifact.metadata && Object.keys(artifact.metadata).length > 0 && (
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : ''}`}>
          <h4 className="font-medium mb-2">Metadata</h4>
          <div className={`p-3 rounded text-sm ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <pre className={`whitespace-pre-wrap ${darkMode ? 'text-gray-300' : ''}`}>{JSON.stringify(artifact.metadata, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
